<template>
  <div class="actividades-control-container">
    <!-- Header Banner -->
    <div class="hero-banner mb-6">
      <div class="hero-content">
        <div class="hero-badge">
          <i class="feather icon-zap"></i> Módulo de Gestión
        </div>
        <h1 class="hero-title">Control de Actividades</h1>
        <p class="hero-subtitle">
          Administración centralizada de actividades diarias, seguimiento de KPIs y validación de entregables por departamento.
        </p>
      </div>
      <div class="hero-illustration">
        <i class="feather icon-activity hero-bg-icon"></i>
      </div>
    </div>

    <!-- Live Stats Widgets -->
    <div class="stats-grid mb-8">
      <div class="stat-card stat-card--blue">
        <div class="stat-card__icon">
          <i class="feather icon-calendar"></i>
        </div>
        <div class="stat-card__info">
          <span class="stat-card__label">Tareas de Hoy</span>
          <h3 class="stat-card__value">{{ resumen.tareasHoy || 0 }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--amber">
        <div class="stat-card__icon">
          <i class="feather icon-clock"></i>
        </div>
        <div class="stat-card__info">
          <span class="stat-card__label">Pendientes por Validar</span>
          <h3 class="stat-card__value">{{ resumen.pendientes || 0 }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--emerald">
        <div class="stat-card__icon">
          <i class="feather icon-check-circle"></i>
        </div>
        <div class="stat-card__info">
          <span class="stat-card__label">Tareas Validadas</span>
          <h3 class="stat-card__value">{{ resumen.validadas || 0 }}</h3>
        </div>
      </div>

      <div class="stat-card stat-card--purple">
        <div class="stat-card__icon">
          <i class="feather icon-trending-up"></i>
        </div>
        <div class="stat-card__info">
          <span class="stat-card__label">Total Este Mes</span>
          <h3 class="stat-card__value">{{ resumen.esteMes || 0 }}</h3>
        </div>
      </div>
    </div>

    <!-- Navigation Modules Section -->

    <!-- LOADING SKELETON mientras se cargan permisos -->
    <div v-if="cargandoPermisos" class="modules-skeleton mb-6">
      <div class="skeleton-label"></div>
      <div class="modules-grid">
        <div v-for="i in 3" :key="i" class="module-card skeleton-card">
          <div class="skeleton-icon"></div>
          <div class="skeleton-line w-3/4 mt-3"></div>
          <div class="skeleton-line w-full mt-2"></div>
          <div class="skeleton-line w-2/3 mt-2"></div>
        </div>
      </div>
    </div>

    <!-- Secciones reales (aparecen juntas tras cargar permisos) -->
    <transition name="fade-up" appear>
      <div v-if="!cargandoPermisos">

    <!-- SECCIÓN 1: MÓDULOS GENERALES (Todos) -->
    <div class="mb-6">
        <h3 class="section-subtitle text-gray-700 font-bold mb-3 flex items-center">
          <i class="feather icon-user text-blue-500 mr-2"></i> Módulos Personales
        </h3>
      <div class="modules-grid">
        <!-- Mis Actividades -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/mis-tareas')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--blue">
              <i class="feather icon-check-square"></i>
            </div>
            <span class="badge badge--blue">Personal</span>
          </div>
          <h4 class="module-card__title">Mis Actividades</h4>
          <p class="module-card__desc">Registra, edita y administra tu bitácora de tareas diarias y avances.</p>
          <div class="module-card__footer">
            <span class="action-link">Acceder al registro <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Mi Dashboard -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/mi-dashboard')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--emerald">
              <i class="feather icon-bar-chart-2"></i>
            </div>
            <span class="badge badge--emerald">Métricas</span>
          </div>
          <h4 class="module-card__title">Mi Dashboard</h4>
          <p class="module-card__desc">Visualiza gráficos de rendimiento individual y metas de KPI alcanzadas.</p>
          <div class="module-card__footer">
            <span class="action-link">Ver mis estadísticas <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>
      </div>
    </div>

    <!-- SECCIÓN 2: GESTIÓN DE ÁREA & SUPERVISIÓN (Jefe de Área / Admin / Root) -->
    <div v-if="esJefeArea || userRoot" class="mb-6">
      <h3 class="section-subtitle text-gray-700 font-bold mb-3 flex items-center">
        <i class="feather icon-briefcase text-purple-500 mr-2"></i> Gestión de Departamento & Supervisión
      </h3>
      <div class="modules-grid">
        <!-- Validar Tareas -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/validar-tareas')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--purple">
              <i class="feather icon-user-check"></i>
            </div>
            <span class="badge badge--purple">Revisión</span>
          </div>
          <h4 class="module-card__title">Validar Tareas</h4>
          <p class="module-card__desc">Revisa, aprueba o retroalimenta las actividades del equipo asignado.</p>
          <div class="module-card__footer">
            <span class="action-link">Validar pendientes <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Gestión de KPIs -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/gestion-kpis')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--amber">
              <i class="feather icon-target"></i>
            </div>
            <span class="badge badge--amber">Supervisión</span>
          </div>
          <h4 class="module-card__title">Gestión de KPIs</h4>
          <p class="module-card__desc">Establece límites mínimos y máximos de productividad por colaborador.</p>
          <div class="module-card__footer">
            <span class="action-link">Configurar KPIs <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Dashboard de Área -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/dashboard-area')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--rose">
              <i class="feather icon-bar-chart"></i>
            </div>
            <span class="badge badge--rose">Métricas</span>
          </div>
          <h4 class="module-card__title">Dashboard de Área</h4>
          <p class="module-card__desc">Analiza indicadores consolidados del equipo y desglose por categorías.</p>
          <div class="module-card__footer">
            <span class="action-link">Ver reporte de área <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Gestión de Mi Equipo (Jefe de Área) -->
        <div class="module-card card-hover-effect" @click="irA('/actividades-control/configuracion')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--slate">
              <i class="feather icon-users"></i>
            </div>
            <span class="badge badge--slate">Departamento</span>
          </div>
          <h4 class="module-card__title">Gestión de Mi Equipo</h4>
          <p class="module-card__desc">Distribuye colaboradores en tu departamento y gestiona las categorías de tu área.</p>
          <div class="module-card__footer">
            <span class="action-link text-primary">Administrar equipo <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>
      </div>
    </div>

    <!-- SECCIÓN 3: ADMINISTRACIÓN & ROOT CONTROL (Root / Admin) -->
    <div v-if="userRoot || esAdministrador" class="mb-6">
      <h3 class="section-subtitle text-gray-700 font-bold mb-3 flex items-center">
        <i class="feather icon-shield text-indigo-600 mr-2"></i> Administración Global & Root Control
      </h3>
      <div class="modules-grid">
        <!-- Configuración de Área / Estructura -->
        <div class="module-card card-hover-effect border-slate" @click="irA('/actividades-control/configuracion')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--slate">
              <i class="feather icon-settings"></i>
            </div>
            <span class="badge badge--slate">Admin / Root</span>
          </div>
          <h4 class="module-card__title">Estructura & Equipos</h4>
          <p class="module-card__desc">Asigna jefes de departamento, configura categorías y distribuye colaboradores.</p>
          <div class="module-card__footer">
            <span class="action-link">Gestionar centro <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Gestión Global de Tareas (Root) -->
        <div v-if="userRoot" class="module-card card-hover-effect border-root" @click="irA('/actividades-control/tareas')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--indigo">
              <i class="feather icon-shield"></i>
            </div>
            <span class="badge badge--root">Root Control</span>
          </div>
          <h4 class="module-card__title">Gestión Global Tareas</h4>
          <p class="module-card__desc">Supervisión total y auditoría de todas las tareas registradas a nivel empresa.</p>
          <div class="module-card__footer">
            <span class="action-link text-indigo">Administrar tareas <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>

        <!-- Gestión Global de Categorías (Root) -->
        <div v-if="userRoot" class="module-card card-hover-effect border-root" @click="irA('/actividades-control/categorias')">
          <div class="module-card__header">
            <div class="icon-wrapper icon-wrapper--cyan">
              <i class="feather icon-layers"></i>
            </div>
            <span class="badge badge--root">Root Control</span>
          </div>
          <h4 class="module-card__title">Catálogo Categorías</h4>
          <p class="module-card__desc">Administración maestra de categorías y tipos de actividad empresariales.</p>
          <div class="module-card__footer">
            <span class="action-link text-indigo">Administrar categorías <i class="feather icon-arrow-right"></i></span>
          </div>
        </div>
      </div>
    </div>

      </div>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'ActividadesControlIndex',
  data() {
    return {
      resumen: {},
      departamentos: [],
      cargandoPermisos: true
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    usuarioId() {
      if (this.auth && this.auth.idusuario) return this.auth.idusuario
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      return u.idusuario || null
    },
    esJefeArea() {
      if (this.userRoot) return true
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      if (u.jefe_departamento == '1' || u.jefe_departamento == 1) return true
      return this.departamentos.some(d => d.tdp_responsable_id == this.usuarioId)
    },
    esAdministrador() {
      if (this.userRoot) return true
      const u = JSON.parse(localStorage.getItem('usuario') || '{}')
      return u.id_group == 1 || u.id_group == '1'
    }
  },
  mounted() {
    this.verificarPermisos()
    this.cargarResumen()
  },
  methods: {
    async verificarPermisos() {
      this.cargandoPermisos = true
      try {
        const res = await this.$http.get(this.$server_url + 'actividades-control/departamentos')
        this.departamentos = res.data || []
      } catch (error) {
        console.error('Error verificando permisos:', error)
      } finally {
        this.cargandoPermisos = false
      }
    },
    async cargarResumen() {
      try {
        if (!this.usuarioId) return
        const hoy = new Date().toISOString().split('T')[0]
        const primerDiaMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
        const ultimoDiaMes = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0]
        const response = await this.$http.get(this.$server_url + 'actividades-control/tareas', {
          params: { idusuario: this.usuarioId }
        })
        const todasTareas = response.data || []
        this.resumen = {
          tareasHoy: todasTareas.filter(t => (t.fecha_tarea || '').substring(0, 10) === hoy).length,
          pendientes: todasTareas.filter(t => t.estado === 'pendiente').length,
          validadas: todasTareas.filter(t => t.estado === 'validada').length,
          esteMes: todasTareas.filter(t => {
            const fecha = (t.fecha_tarea || '').substring(0, 10)
            return fecha >= primerDiaMes && fecha <= ultimoDiaMes
          }).length
        }
      } catch (error) {
        console.error('Error al cargar resumen:', error)
      }
    },
    irA(ruta) {
      this.$router.push(ruta)
    }
  }
}
</script>

<style scoped>
.actividades-control-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
}

/* Hero Banner */
.hero-banner {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 16px;
  padding: 2.2rem 2.5rem;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.25);
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 750px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(99, 102, 241, 0.2);
  border: 1px solid rgba(129, 140, 248, 0.3);
  color: #a5b4fc;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 600;
  margin-bottom: 0.8rem;
}

.hero-title {
  font-size: 1.85rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 0.95rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}

.hero-illustration {
  position: absolute;
  right: -20px;
  bottom: -40px;
  opacity: 0.07;
  pointer-events: none;
}

.hero-bg-icon {
  font-size: 220px;
  color: #ffffff;
}

/* Stats Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 1.25rem;
}

@media (min-width: 640px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.stat-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border: 1px solid #f1f5f9;
  box-shadow: 0 4px 15px -3px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.08);
}

.stat-card__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.stat-card--blue .stat-card__icon { background: #eff6ff; color: #2563eb; }
.stat-card--amber .stat-card__icon { background: #fffbeb; color: #d97706; }
.stat-card--emerald .stat-card__icon { background: #ecfdf5; color: #059669; }
.stat-card--purple .stat-card__icon { background: #faf5ff; color: #7c3aed; }

.stat-card__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 2px;
}

.stat-card__value {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1;
}

/* Modules Grid */
.section-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 1.25rem;
}

@media (min-width: 768px) {
  .modules-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1200px) {
  .modules-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.module-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.5rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.card-hover-effect:hover {
  border-color: #cbd5e1;
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -6px rgba(15, 23, 42, 0.12);
}

.border-root {
  border: 1.5px dashed #818cf8;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.module-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.icon-wrapper--blue { background: #dbeafe; color: #1d4ed8; }
.icon-wrapper--emerald { background: #d1fae5; color: #047857; }
.icon-wrapper--purple { background: #ede9fe; color: #6d28d9; }
.icon-wrapper--amber { background: #fef3c7; color: #b45309; }
.icon-wrapper--rose { background: #ffe4e6; color: #be123c; }
.icon-wrapper--slate { background: #f1f5f9; color: #475569; }
.icon-wrapper--indigo { background: #e0e7ff; color: #4338ca; }
.icon-wrapper--cyan { background: #cffaff; color: #0e7490; }

.badge {
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.badge--blue { background: #eff6ff; color: #1d4ed8; }
.badge--emerald { background: #ecfdf5; color: #047857; }
.badge--purple { background: #faf5ff; color: #6d28d9; }
.badge--amber { background: #fffbeb; color: #b45309; }
.badge--rose { background: #fff1f2; color: #be123c; }
.badge--slate { background: #f8fafc; color: #475569; }
.badge--root { background: #e0e7ff; color: #4338ca; border: 1px solid #c7d2fe; }

.module-card__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.4rem 0;
}

.module-card__desc {
  font-size: 0.86rem;
  color: #64748b;
  margin: 0 0 1.25rem 0;
  line-height: 1.45;
}

.module-card__footer {
  border-top: 1px solid #f1f5f9;
  padding-top: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.action-link {
  font-size: 0.82rem;
  font-weight: 700;
  color: #2563eb;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: gap 0.2s ease;
}

.module-card:hover .action-link {
  gap: 8px;
}

.section-subtitle { font-size: 0.95rem; }

/* Skeleton loading */
.modules-skeleton { animation: pulse 1.5s ease-in-out infinite; }
.skeleton-card { pointer-events: none; background: #f8fafc !important; border-color: #e2e8f0 !important; }
.skeleton-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.skeleton-label {
  height: 20px; width: 220px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 16px;
}
.skeleton-line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}
.w-3\/4 { width: 75%; }
.w-full { width: 100%; }
.w-2\/3 { width: 66%; }
.mt-3 { margin-top: 12px; }
.mt-2 { margin-top: 8px; }
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Fade-up transition para módulos */
.fade-up-enter-active { transition: all 0.4s cubic-bezier(0.22, 0.61, 0.36, 1); }
.fade-up-enter { opacity: 0; transform: translateY(16px); }
</style>
